/**
 * @file date-picker.component.tsx
 * @description Компонент работы с полем дата. Только дата определенного формата. Используется библиотека @dhx/suite для календаря и выбора даты
 * @author AI-Generated | Human-Reviewed
 * @created 2025-09-03
 * @updated 2025-09-16
 * @dependencies ['react-imask', '@src/modules/dhx-suite/dhx-import-fix.js', '@components/ui/base/text-input', '@consta/icons/IconCalendar', 'moment', 'classnames', '@consta/uikit/useGlobalKeys', '@utils/date-time-helper']
 * @related-files ['src/components/ui/base/text-input/text-input.component.tsx']
 */

import { useIMask, IMask, ReactMaskOpts } from 'react-imask';
import { useEffect, useRef, useMemo, useCallback } from 'react';
import { getDHXCalendar, getDHXPopup, getDHXi18n } from '@src/modules/dhx-suite/dhx-import-fix.js';
import '@src/modules/dhx-suite/codebase/suite.min.css';
import { TextInput } from '@components/ui/base/text-input';
import { IconCalendar } from '@consta/icons/IconCalendar';
import moment from 'moment';
import cn from 'classnames';
import { useGlobalKeys } from '@consta/uikit/useGlobalKeys';
import { dateToTimestamp } from '@utils/date-time-helper';

/**
 * Props для компонента выбора даты
 */
interface IDatePickerProps {
    /** Текст метки для поля ввода даты */
    label?: string;
    /** Значение даты в unix timestamp формате */
    value: number | null;
    /** Обработчик изменения даты */
    onChange?: (value: number | null) => void;
    /** Режим только для чтения */
    readOnly?: boolean;
    /** Функция добавления модального окна в очередь */
    addModal: () => void;
    /** Уникальный идентификатор модального окна */
    modalId: string;
    /** Текущая очередь модальных окон */
    currentModalsQueue: string[];
    /** Функция удаления модального окна из очереди по ID */
    removeModalFromQueueById: () => void;
    /** CSS класс для дополнительной стилизации */
    className?: string;
}

export const DatePicker = ({
    label,
    value = moment().unix(),
    onChange,
    readOnly = Boolean(!onChange),
    addModal,
    modalId,
    currentModalsQueue,
    removeModalFromQueueById,
    className,
    ...props
}: IDatePickerProps) => {
    // Получаем DHX компоненты через безопасные функции
    const i18n = getDHXi18n();
    const Calendar = getDHXCalendar();
    const Popup = getDHXPopup();

    i18n.setLocale('calendar', {
        monthsShort: moment.monthsShort(),
        months: moment.months(),
        daysShort: moment.weekdaysShort(),
        days: moment.weekdays(),
        cancel: 'Отмена'
    });
    i18n.setLocale('timepicker', {
        hours: 'Часы',
        minutes: 'Минуты',
        save: 'Сохранить'
    });
    const ref = useRef<HTMLInputElement>(null);
    const itemsRef = useRef<string[] | null>(null);
    const valueRef = useRef<number | null>(value);
    const momentFormat = useMemo(() => 'DD.MM.YYYY', []);
    const datePickerValue = useMemo(() => (value && value > 0 ? moment(value).format(momentFormat) : ''), [value]);
    const {
        ref: inputRef,
        value: iMaskValue,
        setValue
    } = useIMask<HTMLInputElement, ReactMaskOpts>({
        mask: Date,
        pattern: momentFormat,
        min: new Date(1970, 0, 1),
        max: new Date(2030, 0, 1),
        format: (date: Date | null) => (date ? moment(date).format(momentFormat) : ''),
        parse: (str: string) => moment(str, momentFormat).toDate(),
        blocks: {
            YYYY: {
                mask: IMask.MaskedRange,
                from: 1970,
                to: 2030
            },
            MM: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 12
            },
            DD: {
                mask: IMask.MaskedRange,
                from: 1,
                to: 31
            }
        }
        // mark: (date: any) => {
        //     if (moment(date).format('L') === moment().format('L')) {
        //         return 'dhx_calendar-day--current-date';
        //     }
        // }
    });
    const popup = useMemo(() => new Popup(), []);
    const calendar = useMemo(
        () =>
            new Calendar('', {
                dateFormat: '%d.%m.%Y',
                weekStart: 'monday',
                value: datePickerValue || undefined,
                date: datePickerValue || undefined
            }),
        []
    );

    const changeValueHandler = useCallback(
        (newValue: Date | null) => {
            if (typeof onChange !== 'function') {
                return;
            }

            const parsedValue = newValue ? dateToTimestamp(newValue) : null;

            // Проверяем, изменилось ли новое значение (можно сравнить с текущим value, если он доступен)
            if (parsedValue !== valueRef.current) {
                onChange(parsedValue);
            }
        },
        [onChange, momentFormat]
    );

    const showCalendarHandler = () => {
        if (!inputRef.current) {
            return;
        }

        inputRef.current.select();
        ref.current && popup.show(ref.current);
    };

    const changeInputHandler = (newValue: string | null | undefined) => {
        if (!newValue) {
            calendar.clear();
            changeValueHandler(null);
            return;
        }

        if (moment(newValue, momentFormat, true).isValid()) {
            const newValueDate = moment(newValue, momentFormat).toDate();
            calendar.setValue(newValueDate);
            changeValueHandler(newValueDate);
        }

        setValue(newValue);
    };

    popup.attach(calendar);

    useEffect(() => {
        if (datePickerValue) {
            setValue(datePickerValue);
            calendar.setValue(datePickerValue);
        }

        popup.events.on('afterHide', function () {
            removeModalFromQueueById();
            return true;
        });

        popup.events.on('afterShow', function () {
            setTimeout(() => addModal(), 60);
            return true;
        });

        calendar.events.on('change', (date: Date, _oldDate: Date, isClick: boolean) => {
            const newVal = calendar.getValue();
            const stringValue = newVal instanceof Date ? moment(newVal).format(momentFormat) : newVal;
            setValue(stringValue);

            // Вызываем changeValueHandler лишь один раз, если изменение произошло по клику
            if (isClick) {
                changeValueHandler(date);
            }

            popup.hide();
        });

        return () => {
            calendar && calendar.destructor();
        };
    }, []);

    useEffect(() => {
        datePickerValue ? calendar.setValue(datePickerValue) : calendar.clear();
        calendar.paint();
    }, [datePickerValue]);

    useEffect(() => {
        itemsRef.current = currentModalsQueue;
    }, [currentModalsQueue]);

    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    useGlobalKeys({
        Escape: () => {
            if (itemsRef.current?.at(-1) === modalId) {
                popup.hide();
            }
        }
    });

    return (
        <TextInput
            size="s"
            iconSize="xs"
            label={label}
            value={iMaskValue}
            ref={ref}
            inputRef={inputRef}
            readOnly={readOnly}
            rightSide={IconCalendar}
            onChange={changeInputHandler}
            {...props}
            onFocus={() => (!readOnly ? showCalendarHandler() : null)}
            className={cn('DatePickerXMes', className, { 'cursor-pointer': !readOnly })}
        />
    );
};
